import { create as createQr } from "qrcode";

interface QrCodeProps {
  /** What the code encodes. */
  value: string;
  /** Accessible description. The sheet is printed, but this renders on screen too. */
  title: string;
  className?: string;
}

/**
 * A QR code as inline SVG, rendered synchronously.
 *
 * Vector, not canvas: this goes on paper, and a canvas would print at whatever
 * raster resolution it happened to be sized at. Synchronous for a second
 * reason: the sheet calls `window.print()` as soon as fonts and images settle,
 * and an async QR could still be resolving at that moment. `create()` does the
 * encoding in-process and we draw the modules ourselves, so there is nothing to
 * wait for.
 *
 * Error correction M. Verified against the printed design: the code still
 * decodes at 28% of its printed size, so a phone at arm's length or a poor
 * office print has plenty of margin. A higher level would only shrink the
 * modules for no practical gain.
 */
export function QrCode({ value, title, className }: QrCodeProps) {
  const { modules } = createQr(value, { errorCorrectionLevel: "M" });
  const size = modules.size;

  // One path for every dark module. Cheaper than a rect per module (this is a
  // ~37×37 grid, so upwards of 600 nodes) and prints identically.
  let path = "";
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (modules.data[y * size + x]) {
        path += `M${x} ${y}h1v1h-1z`;
      }
    }
  }

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      role="img"
      aria-label={title}
      // crispEdges keeps module boundaries on whole device pixels, so the code
      // stays scannable on screen previews as well as in print.
      shapeRendering="crispEdges"
    >
      <path d={path} fill="currentColor" />
    </svg>
  );
}
